import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import * as dat from 'lil-gui'

/**
 * Base
 */
// Debug
const gui = new dat.GUI()

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()


/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader()
const particlesTexture = textureLoader.load('/1.png')
const rainTexture = textureLoader.load('/2.png')

const doorColorTexture = textureLoader.load('/textures/door/color.jpg')
const doorAlphaTexture = textureLoader.load('/textures/door/alpha.jpg')
const doorAmbientOcclusionTexture = textureLoader.load('/textures/door/ambientOcclusion.jpg')
const doorHeightTexture = textureLoader.load('/textures/door/height.jpg')
const doorNormalTexture = textureLoader.load('/textures/door/normal.jpg')
const doorMetalnessTexture = textureLoader.load('/textures/door/metalness.jpg')
const doorRoughnessTexture = textureLoader.load('/textures/door/roughness.jpg')

const bricksColorTexture = textureLoader.load('/textures/bricks/color.jpg')
const bricksAmbientOcclusionTexture = textureLoader.load('/textures/bricks/ambientOcclusion.jpg')
const bricksNormalTexture = textureLoader.load('/textures/bricks/normal.jpg')
const bricksRoughnessTexture = textureLoader.load('/textures/bricks/roughness.jpg')

const grassColorTexture = textureLoader.load('/textures/grass/color.jpg')
const grassAmbientOcclusionTexture = textureLoader.load('/textures/grass/ambientOcclusion.jpg')
const grassNormalTexture = textureLoader.load('/textures/grass/normal.jpg')
const grassRoughnessTexture = textureLoader.load('/textures/grass/roughness.jpg')

grassColorTexture.repeat.set(8, 8)
grassAmbientOcclusionTexture.repeat.set(8, 8)
grassNormalTexture.repeat.set(8, 8)
grassRoughnessTexture.repeat.set(8, 8)

grassColorTexture.wrapS = THREE.RepeatWrapping
grassAmbientOcclusionTexture.wrapS = THREE.RepeatWrapping
grassNormalTexture.wrapS = THREE.RepeatWrapping
grassRoughnessTexture.wrapS = THREE.RepeatWrapping

grassColorTexture.wrapT = THREE.RepeatWrapping
grassAmbientOcclusionTexture.wrapT = THREE.RepeatWrapping
grassNormalTexture.wrapT = THREE.RepeatWrapping
grassRoughnessTexture.wrapT = THREE.RepeatWrapping

/**
 * House
 */
// House container
const house = new THREE.Group()
scene.add(house)

// Walls
const walls = new THREE.Mesh(
    new THREE.BoxGeometry(4, 2.5, 4),
    new THREE.MeshStandardMaterial({
        map: bricksColorTexture,
        aoMap: bricksAmbientOcclusionTexture,
        normalMap: bricksNormalTexture,
        roughnessMap: bricksRoughnessTexture
    })
)
walls.castShadow = true
walls.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(walls.geometry.attributes.uv.array, 2))
walls.position.y = 1.25
house.add(walls)

// Roof
const roof = new THREE.Mesh(
    new THREE.ConeGeometry(3.5, 1, 4),
    new THREE.MeshStandardMaterial({ color: '#b35f45' })
)
roof.rotation.y = Math.PI * 0.25
roof.position.y = 2.5 + 0.5
house.add(roof)

// Door
const door = new THREE.Mesh(
    new THREE.PlaneGeometry(2, 2, 100, 100),
    new THREE.MeshStandardMaterial({
        map: doorColorTexture,
        transparent: true,
        alphaMap: doorAlphaTexture,
        aoMap: doorAmbientOcclusionTexture,
        displacementMap: doorHeightTexture,
        displacementScale: 0.1,
        normalMap: doorNormalTexture,
        metalnessMap: doorMetalnessTexture,
        roughnessMap: doorRoughnessTexture
    })
)
door.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(door.geometry.attributes.uv.array, 2))
door.position.y = 1
door.position.z = 2 + 0.01
house.add(door)

// Bushes
const bushGeometry = new THREE.SphereGeometry(1, 16, 16)
const bushMaterial = new THREE.MeshStandardMaterial({ color: '#89c854' })

const bush1 = new THREE.Mesh(bushGeometry, bushMaterial)
bush1.castShadow = true
bush1.scale.set(0.5, 0.5, 0.5)
bush1.position.set(0.8, 0.2, 2.2)

const bush2 = new THREE.Mesh(bushGeometry, bushMaterial)
bush2.castShadow = true
bush2.scale.set(0.25, 0.25, 0.25)
bush2.position.set(1.4, 0.1, 2.1)

const bush3 = new THREE.Mesh(bushGeometry, bushMaterial)
bush3.castShadow = true
bush3.scale.set(0.4, 0.4, 0.4)
bush3.position.set(- 0.8, 0.1, 2.2)

const bush4 = new THREE.Mesh(bushGeometry, bushMaterial)
bush4.castShadow = true
bush4.scale.set(0.15, 0.15, 0.15)
bush4.position.set(- 1, 0.05, 2.6)

house.add(bush1, bush2, bush3, bush4)

// Floor
const floor = new THREE.Mesh(
    new THREE.PlaneGeometry(10, 10),
    new THREE.MeshStandardMaterial({
        map: grassColorTexture,
        aoMap: grassAmbientOcclusionTexture,
        normalMap: grassNormalTexture,
        roughnessMap: grassRoughnessTexture
    })
)
floor.receiveShadow = true
floor.geometry.setAttribute('uv2', new THREE.Float32BufferAttribute(floor.geometry.attributes.uv.array, 2))
floor.rotation.x = - Math.PI * 0.5
floor.position.y = 0
scene.add(floor)

/**
 * Particles
 */


const cloud = new THREE.Group()
scene.add(cloud)

const geometry = new THREE.BufferGeometry()
const positions = new Float32Array(150 * 3)

for(let i = 0; i < 150*3; i++)
{
    const i3 = i * 3

    positions[i3    ] = (Math.random() - 0.5)
    positions[i3 + 1] = (Math.random() - 0.5)
    positions[i3 + 2] = (Math.random() - 0.5)
}

geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3))

const particlesMaterial = new THREE.PointsMaterial({
    size: 0.15,
    sizeAttenuation: true,
    color : '#d6d5c5',
    transparent : true,
    alphaMap : particlesTexture,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})

const points = new THREE.Points(geometry, particlesMaterial)
const points1 = new THREE.Points(geometry, particlesMaterial)
const points2 = new THREE.Points(geometry, particlesMaterial)
const points3 = new THREE.Points(geometry, particlesMaterial)
const points4 = new THREE.Points(geometry, particlesMaterial)
points1.position.x = 0.9
points2.position.x = -0.9
points3.position.y = 0.5
points3.position.x = 0.5
points4.position.y = 0.5
points4.position.x = - 0.5
cloud.add(points,points1,points2,points3,points4)

cloud.position.y = 5.5
cloud.position.x = -3.5


const cloud1 = new THREE.Group()
scene.add(cloud1)

const points6 = new THREE.Points(geometry, particlesMaterial)
const points7 = new THREE.Points(geometry, particlesMaterial)
const points8 = new THREE.Points(geometry, particlesMaterial)
const points9 = new THREE.Points(geometry, particlesMaterial)
const points10 = new THREE.Points(geometry, particlesMaterial)
points6.position.x = 0.9
points7.position.x = -0.9
points8.position.y = 0.5
points8.position.x = 0.5
points9.position.y = 0.5
points9.position.x = - 0.5
cloud1.add(points6,points7,points8,points9,points10)

cloud1.position.y = 5.5


const cloud2 = new THREE.Group()
scene.add(cloud2)

const points11 = new THREE.Points(geometry, particlesMaterial)
const points12 = new THREE.Points(geometry, particlesMaterial)
const points13 = new THREE.Points(geometry, particlesMaterial)
const points14 = new THREE.Points(geometry, particlesMaterial)
const points15 = new THREE.Points(geometry, particlesMaterial)
points11.position.x = 0.9
points12.position.x = -0.9
points13.position.y = 0.5
points13.position.x = 0.5
points14.position.y = 0.5
points14.position.x = - 0.5
cloud2.add(points11,points12,points13,points14,points15)

cloud2.position.y = 5.5
cloud2.position.x = 3.5

/*
const rain = new THREE.Group()
scene.add(rain)

const rainGeometry = new THREE.BufferGeometry()
const rainPositions = new Float32Array(80 * 3)

for(let i = 0; i < 80*3; i++)
{
    const i3 = i * 3

    rainPositions[i3    ] = (Math.random() - 0.5)
    rainPositions[i3 + 1] = (Math.random() - 0.5)
    rainPositions[i3 + 2] = (Math.random() - 0.5)
}

rainGeometry.setAttribute('position', new THREE.BufferAttribute(rainPositions, 3))

const rainMaterial = new THREE.PointsMaterial({
    size: 0.4,
    sizeAttenuation: true,
    color : '#00008B',
    transparent : true,
    alphaMap : rainTexture,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})
const rainPoint = new THREE.Points(rainGeometry, rainMaterial)
rainPoint.position.x = 0
rainPoint.position.y = 4.3
rain.add(rainPoint)

*/
const rainGeometry = new THREE.BufferGeometry()
const rainPositions = new Float32Array(150 * 3)
for(let i = 0; i < 150*3; i++)
{
    const i3 = i * 3

    rainPositions[i3    ] = (Math.random() *8)-4
    rainPositions[i3 + 1] = (Math.random() *4.5)-4
    rainPositions[i3 + 2] = (Math.random() *4)-2
}
rainGeometry.setAttribute('position', new THREE.BufferAttribute(rainPositions, 3))

const rainMaterial = new THREE.PointsMaterial({
    size: 0.4,
    sizeAttenuation: true,
    color : '#0287c3',
    transparent : true,
    alphaMap : rainTexture,
    depthWrite: false,
    blending: THREE.AdditiveBlending
})
const rainPoint = new THREE.Points(rainGeometry, rainMaterial)
rainPoint.position.y = 4.3
scene.add(rainPoint)

const ambientLight = new THREE.AmbientLight(0xffffff,0.5)
scene.add(ambientLight)

const fog = new THREE.Fog('#5A5A5A', 1, 30)
scene.fog = fog

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.z = 10
camera.position.y = 3
camera.position.x = 0
scene.add(camera)

// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true

/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))


/**
 * Animate
 */
const clock = new THREE.Clock()

const tick = () =>
{
    const elapsedTime = clock.getElapsedTime()

    for(let i=0;i<150;i++){
        const i3 = i * 3
        rainGeometry.attributes.position.array[i3+1] = Math.tan((Math.random()-2)*2)-2
    }
    
    rainGeometry.attributes.position.needsUpdate = true

    // Update controls 
    controls.update()

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()